import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  FolderPlus, Upload, FileText, Link as LinkIcon, Search, Trash2,
  Pencil, ExternalLink, Download, FolderOpen, X, Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { useEntitlements } from "@/hooks/use-entitlements";
import { UpgradeOverlay } from "@/components/upgrade-overlay";
import {
  listDocuments, createDocument, updateDocument, deleteDocument,
  createFolder, deleteFolder, createDocumentUploadUrl,
} from "@/lib/documents.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/documents")({ component: DocumentsAdmin });

function formatBytes(n?: number | null) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function DocumentsAdmin() {
  const ent = useEntitlements();
  const qc = useQueryClient();
  const listFn = useServerFn(listDocuments);
  const createFolderFn = useServerFn(createFolder);
  const deleteFolderFn = useServerFn(deleteFolder);
  const createDocFn = useServerFn(createDocument);
  const updateDocFn = useServerFn(updateDocument);
  const deleteDocFn = useServerFn(deleteDocument);
  const createUploadFn = useServerFn(createDocumentUploadUrl);

  const [search, setSearch] = React.useState("");
  const [filterFolder, setFilterFolder] = React.useState<string>("all");
  const [filterType, setFilterType] = React.useState<string>("all");
  const [filterVisible, setFilterVisible] = React.useState<string>("all");
  const [openUpload, setOpenUpload] = React.useState(false);
  const [openFolder, setOpenFolder] = React.useState(false);

  const q = useQuery({
    queryKey: ["documents"],
    enabled: ent.documentsEnabled,
    queryFn: () => listFn({}),
  });

  const folders = q.data?.folders ?? [];
  const docs = q.data?.documents ?? [];

  const filtered = React.useMemo(() => {
    return docs.filter((d: any) => {
      if (filterFolder !== "all" && d.folder_id !== filterFolder) return false;
      if (filterType !== "all" && d.doc_type !== filterType) return false;
      if (filterVisible === "admin" && !d.visible_admin) return false;
      if (filterVisible === "teacher" && !d.visible_teacher) return false;
      if (filterVisible === "student" && !d.visible_student) return false;
      if (search && !d.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [docs, search, filterFolder, filterType, filterVisible]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["documents"] });

  if (!ent.isLoading && !ent.documentsEnabled) {
    return (
      <PortalLayout portal="admin" breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Tài liệu" }]}>
        <UpgradeOverlay
          feature="Thư viện Tài liệu"
          description="Quản lý kho tài liệu của trung tâm: tải file (PDF, ảnh, video), chia sẻ link YouTube/Drive, phân quyền cho từng nhóm Admin / Giáo viên / Học sinh."
        />
      </PortalLayout>
    );
  }

  return (
    <PortalLayout portal="admin" breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Tài liệu" }]}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="font-display text-2xl font-semibold">Thư viện tài liệu</h1>
            <p className="text-sm text-muted-foreground">Quản lý và chia sẻ tài liệu trung tâm</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={openFolder} onOpenChange={setOpenFolder}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5"><FolderPlus className="size-4" />Tạo danh mục</Button>
              </DialogTrigger>
              <NewFolderDialog onClose={() => setOpenFolder(false)} onCreate={async (name) => {
                try { await createFolderFn({ data: { name } }); toast.success("Đã tạo danh mục"); refresh(); setOpenFolder(false); }
                catch (e: any) { toast.error(e.message); }
              }} />
            </Dialog>
            <Dialog open={openUpload} onOpenChange={setOpenUpload}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5" style={{ background: "var(--portal)", color: "white" }}>
                  <Upload className="size-4" />Tải lên tài liệu
                </Button>
              </DialogTrigger>
              <UploadDialog
                folders={folders}
                onClose={() => setOpenUpload(false)}
                onSubmit={async (payload) => {
                  try {
                    if (payload.doc_type === "file" && payload.file) {
                      const { path, token } = await createUploadFn({ data: { filename: payload.file.name } });
                      const { error } = await supabase.storage.from("documents").uploadToSignedUrl(path, token, payload.file);
                      if (error) throw error;
                      await createDocFn({ data: {
                        title: payload.title, description: payload.description, folder_id: payload.folder_id,
                        doc_type: "file", file_path: path, file_size: payload.file.size, mime_type: payload.file.type,
                        visible_admin: payload.visible_admin, visible_teacher: payload.visible_teacher, visible_student: payload.visible_student,
                      } as any });
                    } else {
                      await createDocFn({ data: {
                        title: payload.title, description: payload.description, folder_id: payload.folder_id,
                        doc_type: "link", external_url: payload.external_url,
                        visible_admin: payload.visible_admin, visible_teacher: payload.visible_teacher, visible_student: payload.visible_student,
                      } as any });
                    }
                    toast.success("Đã thêm tài liệu");
                    refresh();
                    setOpenUpload(false);
                  } catch (e: any) { toast.error(e.message); }
                }}
              />
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border rounded-2xl p-4 mb-5 flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Tìm tài liệu..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={filterFolder} onValueChange={setFilterFolder}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Danh mục" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả danh mục</SelectItem>
              {folders.map((f: any) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả loại</SelectItem>
              <SelectItem value="file">File</SelectItem>
              <SelectItem value="link">Link</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterVisible} onValueChange={setFilterVisible}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả hiển thị</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="teacher">Giáo viên</SelectItem>
              <SelectItem value="student">Học sinh</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Folders */}
        {folders.length > 0 && (
          <div className="mb-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Danh mục</div>
            <div className="flex flex-wrap gap-2">
              {folders.map((f: any) => (
                <div key={f.id} className="group flex items-center gap-2 px-3 py-1.5 rounded-full border bg-card text-sm">
                  <FolderOpen className="size-3.5 text-blue-500" />
                  <span>{f.name}</span>
                  <button
                    onClick={async () => {
                      if (!confirm(`Xoá danh mục "${f.name}"?`)) return;
                      try { await deleteFolderFn({ data: { id: f.id } }); toast.success("Đã xoá"); refresh(); }
                      catch (e: any) { toast.error(e.message); }
                    }}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition"
                  ><X className="size-3" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Docs grid */}
        {q.isLoading ? (
          <div className="text-center py-8 text-muted-foreground"><Loader2 className="size-5 inline animate-spin" /> Đang tải...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-2xl">
            <FolderOpen className="size-10 mx-auto mb-2 opacity-30" />
            Chưa có tài liệu nào
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((d: any) => (
              <DocCard
                key={d.id}
                doc={d}
                folderName={folders.find((f: any) => f.id === d.folder_id)?.name}
                onDelete={async () => {
                  if (!confirm(`Xoá "${d.title}"?`)) return;
                  try { await deleteDocFn({ data: { id: d.id } }); toast.success("Đã xoá"); refresh(); }
                  catch (e: any) { toast.error(e.message); }
                }}
                onToggle={async (field, value) => {
                  try { await updateDocFn({ data: { id: d.id, [field]: value } as any }); refresh(); }
                  catch (e: any) { toast.error(e.message); }
                }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </PortalLayout>
  );
}

function DocCard({ doc, folderName, onDelete, onToggle }: {
  doc: any; folderName?: string; onDelete: () => void;
  onToggle: (field: "visible_admin" | "visible_teacher" | "visible_student", value: boolean) => void;
}) {
  const isFile = doc.doc_type === "file";
  const Icon = isFile ? FileText : LinkIcon;
  const open = () => {
    const url = isFile ? doc.signed_url : doc.external_url;
    if (url) window.open(url, "_blank");
  };
  return (
    <div className="bg-card border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="size-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 grid place-items-center shrink-0">
          <Icon className="size-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{doc.title}</div>
          {doc.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{doc.description}</div>}
          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground">
            {folderName && <Badge variant="outline" className="text-[10px]">{folderName}</Badge>}
            <span>{isFile ? formatBytes(doc.file_size) : "Link"}</span>
            <span>· {format(new Date(doc.created_at), "dd/MM/yy", { locale: vi })}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <label className="flex items-center gap-1.5"><Switch checked={!!doc.visible_admin} onCheckedChange={(v) => onToggle("visible_admin", v)} /> Admin</label>
        <label className="flex items-center gap-1.5"><Switch checked={!!doc.visible_teacher} onCheckedChange={(v) => onToggle("visible_teacher", v)} /> GV</label>
        <label className="flex items-center gap-1.5"><Switch checked={!!doc.visible_student} onCheckedChange={(v) => onToggle("visible_student", v)} /> HS</label>
      </div>
      <div className="flex items-center gap-2 pt-2 border-t">
        <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={open}>
          {isFile ? <Download className="size-3.5" /> : <ExternalLink className="size-3.5" />}
          {isFile ? "Tải/Xem" : "Mở"}
        </Button>
        <Button size="sm" variant="ghost" className="text-red-500" onClick={onDelete}><Trash2 className="size-4" /></Button>
      </div>
    </div>
  );
}

function NewFolderDialog({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string) => void }) {
  const [name, setName] = React.useState("");
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>Tạo danh mục mới</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2">
        <Label>Tên danh mục</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="VD: Giáo trình Toán" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Huỷ</Button>
        <Button onClick={() => name.trim() && onCreate(name.trim())}>Tạo</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function UploadDialog({ folders, onClose, onSubmit }: {
  folders: any[];
  onClose: () => void;
  onSubmit: (p: {
    title: string; description: string; folder_id: string | null;
    doc_type: "file" | "link"; file?: File; external_url?: string;
    visible_admin: boolean; visible_teacher: boolean; visible_student: boolean;
  }) => Promise<void>;
}) {
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [folderId, setFolderId] = React.useState<string>("none");
  const [docType, setDocType] = React.useState<"file" | "link">("file");
  const [file, setFile] = React.useState<File | null>(null);
  const [url, setUrl] = React.useState("");
  const [vAdmin, setVAdmin] = React.useState(true);
  const [vTeacher, setVTeacher] = React.useState(true);
  const [vStudent, setVStudent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Tải lên tài liệu</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
        <div>
          <Label>Loại</Label>
          <div className="flex gap-2 mt-1.5">
            <Button type="button" size="sm" variant={docType === "file" ? "default" : "outline"} onClick={() => setDocType("file")} className="flex-1 gap-1.5"><FileText className="size-4" />File</Button>
            <Button type="button" size="sm" variant={docType === "link" ? "default" : "outline"} onClick={() => setDocType("link")} className="flex-1 gap-1.5"><LinkIcon className="size-4" />Link</Button>
          </div>
        </div>
        <div>
          <Label>Tiêu đề</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tên tài liệu" />
        </div>
        <div>
          <Label>Mô tả (tuỳ chọn)</Label>
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <Label>Danh mục</Label>
          <Select value={folderId} onValueChange={setFolderId}>
            <SelectTrigger><SelectValue placeholder="Không có" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Không có —</SelectItem>
              {folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {docType === "file" ? (
          <div>
            <Label>File</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            {file && <div className="text-xs text-muted-foreground mt-1">{file.name} · {formatBytes(file.size)}</div>}
          </div>
        ) : (
          <div>
            <Label>URL</Label>
            <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
          </div>
        )}
        <div className="pt-2 border-t">
          <Label className="mb-2 block">Hiển thị cho</Label>
          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm"><span>Admin</span><Switch checked={vAdmin} onCheckedChange={setVAdmin} /></label>
            <label className="flex items-center justify-between text-sm"><span>Giáo viên</span><Switch checked={vTeacher} onCheckedChange={setVTeacher} /></label>
            <label className="flex items-center justify-between text-sm"><span>Học sinh</span><Switch checked={vStudent} onCheckedChange={setVStudent} /></label>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Huỷ</Button>
        <Button
          disabled={busy || !title.trim() || (docType === "file" ? !file : !url.trim())}
          onClick={async () => {
            setBusy(true);
            try {
              await onSubmit({
                title: title.trim(), description: description.trim(),
                folder_id: folderId === "none" ? null : folderId,
                doc_type: docType, file: file ?? undefined, external_url: url.trim() || undefined,
                visible_admin: vAdmin, visible_teacher: vTeacher, visible_student: vStudent,
              });
            } finally { setBusy(false); }
          }}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : "Lưu"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
